import { useCallback, useEffect, useState } from 'react';
import type { ClinicalSite } from '@starter/shared';
import { useProgramId } from '../../auth/AuthContext.js';
import { api, ApiError } from '../../api/client.js';
import { ErrorBanner } from '../../components/ErrorBanner.js';
import { LoadingState } from '../../components/LoadingState.js';
import { Modal } from '../../components/Modal.js';
import { PageHeader } from '../../components/PageHeader.js';
import { StatusBadge } from '../../components/StatusBadge.js';

type FormMode = 'create' | 'edit' | null;

export function ClinicalSitesPage() {
  const programId = useProgramId();
  const [sites, setSites] = useState<ClinicalSite[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<FormMode>(null);
  const [editing, setEditing] = useState<ClinicalSite | null>(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [addressLine1, setAddressLine1] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [notes, setNotes] = useState('');
  const [status, setStatus] = useState<'active' | 'inactive'>('active');

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    api
      .listClinicalSites({ programId, page: 1, pageSize: 100 })
      .then((res) => setSites(res.data))
      .catch((err) => setError(err instanceof ApiError ? err.message : 'Failed to load sites'))
      .finally(() => setLoading(false));
  }, [programId]);

  useEffect(load, [load]);

  function openCreate() {
    setEditing(null);
    setName('');
    setAddressLine1('');
    setCity('');
    setState('');
    setPostalCode('');
    setNotes('');
    setStatus('active');
    setFormError(null);
    setMode('create');
  }

  function openEdit(site: ClinicalSite) {
    setEditing(site);
    setName(site.name);
    setAddressLine1(site.addressLine1 ?? '');
    setCity(site.city ?? '');
    setState(site.state ?? '');
    setPostalCode(site.postalCode ?? '');
    setNotes(site.notes ?? '');
    setStatus(site.status);
    setFormError(null);
    setMode('edit');
  }

  function closeModal() {
    setMode(null);
    setEditing(null);
    setFormError(null);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setFormError(null);
    try {
      if (mode === 'create') {
        await api.createClinicalSite({
          programId,
          name,
          addressLine1: addressLine1 || null,
          addressLine2: null,
          city: city || null,
          state: state || null,
          postalCode: postalCode || null,
          notes: notes || null,
        });
      } else if (mode === 'edit' && editing) {
        await api.updateClinicalSite(editing.id, {
          name,
          addressLine1: addressLine1 || null,
          addressLine2: null,
          city: city || null,
          state: state || null,
          postalCode: postalCode || null,
          notes: notes || null,
          status,
        });
      }
      closeModal();
      load();
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="page">
      <PageHeader
        title="Clinical Sites"
        subtitle="Hospitals and facilities where students rotate"
        actions={
          <button type="button" className="btn btn--primary" onClick={openCreate}>
            Add site
          </button>
        }
      />

      {error && <ErrorBanner message={error} onRetry={load} />}

      {loading ? (
        <LoadingState message="Loading clinical sites…" />
      ) : (
        <div className="card">
          {sites.length === 0 ? (
            <p className="text-muted">No clinical sites configured yet.</p>
          ) : (
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Location</th>
                    <th>Status</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {sites.map((s) => (
                    <tr key={s.id}>
                      <td>{s.name}</td>
                      <td>
                        {[s.city, s.state].filter(Boolean).join(', ') || '—'}
                      </td>
                      <td>
                        <StatusBadge status={s.status} variant={s.status === 'active' ? 'success' : 'muted'} />
                      </td>
                      <td>
                        <button type="button" className="btn btn--ghost btn--sm" onClick={() => openEdit(s)}>
                          Edit
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <Modal
        open={mode !== null}
        title={mode === 'create' ? 'Add clinical site' : 'Edit clinical site'}
        onClose={closeModal}
        footer={
          <div className="form-actions">
            <button type="button" className="btn btn--ghost" onClick={closeModal}>
              Cancel
            </button>
            <button type="submit" form="site-form" className="btn btn--primary" disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        }
      >
        <form id="site-form" onSubmit={handleSubmit}>
          {formError && <div className="banner banner--error" role="alert">{formError}</div>}
          <label className="field">
            <span className="field__label">Site name</span>
            <input value={name} onChange={(e) => setName(e.target.value)} required />
          </label>
          <label className="field">
            <span className="field__label">Address</span>
            <input value={addressLine1} onChange={(e) => setAddressLine1(e.target.value)} />
          </label>
          <div className="form-row">
            <label className="field">
              <span className="field__label">City</span>
              <input value={city} onChange={(e) => setCity(e.target.value)} />
            </label>
            <label className="field">
              <span className="field__label">State</span>
              <input value={state} onChange={(e) => setState(e.target.value)} />
            </label>
            <label className="field">
              <span className="field__label">Postal code</span>
              <input value={postalCode} onChange={(e) => setPostalCode(e.target.value)} />
            </label>
          </div>
          <label className="field">
            <span className="field__label">Notes</span>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} />
          </label>
          {mode === 'edit' && (
            <label className="field">
              <span className="field__label">Status</span>
              <select value={status} onChange={(e) => setStatus(e.target.value as 'active' | 'inactive')}>
                <option value="active">active</option>
                <option value="inactive">inactive</option>
              </select>
            </label>
          )}
        </form>
      </Modal>
    </div>
  );
}
